import {
  configureApi,
  configureAssets,
  configureContentLanguage,
  configureContentLanguageStorage,
  getContentLanguage,
  getStoredContentLanguage,
} from "@repo/shared/api";
import {
  initI18n,
  resolveUiLocale,
  uiLocalePrimarySubtag,
} from "@repo/shared/i18n";
import { promptOutOfCredits } from "@repo/shared/redux";
import { setCreditsBridge } from "@repo/shared/redux";
import {
  creditsExhausted,
  creditsHeaderReceived,
  fetchCredits,
} from "@repo/shared/redux";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";

import App from "./App";
import {
  onRequest,
  onRequestError,
  onResponse,
  onResponseError,
} from "./api/interceptors";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { GAME_ASSET_SLUG } from "./config/game";
import {
  CONTENT_LANGUAGE_STORAGE_KEY,
  URL_LOCALE,
  currentPathUnderLocale,
  detectBrowserContentLanguage,
} from "./config/localePath";
import "./css/webapp-formations.css";
import { preloadMixpanel, withMixpanel } from "./events/mixpanelClient";
import {
  captureInitialChannel,
  getInitialChannel,
} from "./events/mixpanelContext";
import "./index.css";
import { registerPermissionsAdapters } from "./redux/permissionsAdapters";
import store from "./redux/store";
import { safeGetItem, safeSetItem } from "./utils/safeStorage";
import { filterSentryNoise } from "./utils/sentryNoise";
import { registerWebStoragePort } from "./utils/sharedStoragePort";
import { installTranslationDomGuard } from "./utils/translationDomGuard";

// Before the first render: browser translation detaches the text nodes React
// renders, and without this the next insertBefore/removeChild on one of them
// crashes the page.
installTranslationDomGuard();

// Shared code reads localStorage through this port, so register it before
// anything that touches the store runs.
registerWebStoragePort();

// The shared permissions module reads the user, the A/B arm and signup
// attribution through ports; this plugs web's implementations in, before any
// render.
registerPermissionsAdapters();

// Snapshot the channel into localStorage, then attach it as a super property so
// it rides on every event from this browser — anonymous visitors included.
// register_once won't overwrite an existing value, matching the localStorage lock.
captureInitialChannel();
withMixpanel((mp) => mp.register_once(getInitialChannel()));

withMixpanel((mp) => mp.register({ platform: "web" }));

// Before configureApi so the first fetches already carry X-Content-Language.
// The page language is the URL's locale and nothing else; landing on a
// prefixed URL is not a pick, so storage is left alone here. The stored
// preference is written by the picker and the profile mirror only.
configureContentLanguage(URL_LOCALE ?? "en-US");
configureContentLanguageStorage({
  get: () => safeGetItem(CONTENT_LANGUAGE_STORAGE_KEY),
  set: (language) => safeSetItem(CONTENT_LANGUAGE_STORAGE_KEY, language),
});

// Which language this load renders in, decided before anything renders so a
// redirect costs one HTML load instead of mounting, starting font and chunk
// downloads, and reloading mid-download (DEV-3821).
//
// Logged in: the profile, which fetchUser mirrors into storage on every
// authenticated load. fetchUser still redirects when the profile changed on
// another device, so a stale mirror costs one extra hop.
// Anonymous on an unprefixed URL (DEV-3831): the stored pick if the picker was
// ever used (an explicit "en-US" counts and stops the fallback), else the
// browser's language when it is one we have. The browser language is a guess
// and is never stored: the login claim would push it onto a profile that
// picked something else. Browser language rather than IP country, so a pt-BR
// visitor lands on Portuguese and a crawler (US English renderer) stays put.
// Anonymous on a prefixed URL: the URL, always. A shared /de link renders
// German for everyone, and nothing here writes storage.
const loggedIn = !!safeGetItem("access_token");
const storedLanguage = getStoredContentLanguage();
const targetLanguage = loggedIn
  ? storedLanguage
  : URL_LOCALE
    ? null
    : (storedLanguage ?? detectBrowserContentLanguage());
const languageRedirect =
  targetLanguage && targetLanguage !== getContentLanguage()
    ? currentPathUnderLocale(targetLanguage)
    : null;
if (languageRedirect) window.location.replace(languageRedirect);

// UI locale follows the content language (DEV-3600). `lang` is the primary
// subtag of the locale the chrome actually renders in, so German and Spanish
// stay "en" while their chrome is English.
const uiLocale = resolveUiLocale(getContentLanguage());
document.documentElement.lang = uiLocalePrimarySubtag(uiLocale);

configureApi(
  import.meta.env.VITE_APP_BACKEND_URL,
  onRequest,
  onResponse,
  onRequestError,
  onResponseError,
);

configureAssets({ gameSlug: GAME_ASSET_SLUG });

// Connects the response interceptor to the store. The interceptor can't import
// the store itself without creating a cycle (store -> permissionsSlice ->
// privateApi -> interceptors), so it emits into a registry we fill here.
setCreditsBridge({
  onHeaders: (payload) => store.dispatch(creditsHeaderReceived(payload)),
  onInsufficientCredits: ({ balanceApplied, featureId }) => {
    // The refusal usually carries the exact balance in its headers, which has
    // already been applied; zeroing on top of that would be a guess, and a 402
    // means "less than this feature costs", not "none left".
    if (!balanceApplied) store.dispatch(creditsExhausted());
    // The club exists but the balance is spent, so this is the upgrade moment
    // rather than the import gate.
    store.dispatch(promptOutOfCredits(featureId));
  },
  // The call went through and took the last credit: same dialog, raised now
  // instead of on the next click that would be refused. The refetch picks up
  // the challenges paid for this period, which the gate lets through at zero.
  onLastCreditSpent: ({ featureId }) => {
    store.dispatch(promptOutOfCredits(featureId, "spent"));
    store.dispatch(fetchCredits());
  },
});

// Only initialize Sentry if DSN is provided (not in localhost/development).
// Every visitor, not only paid ones: the errors worth catching happen on the
// free and logged-out paths too.
if (import.meta.env.VITE_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENVIRONMENT,
    // 20% of error events, dropped at random before they leave the browser.
    // Every visitor reports now, not only paid ones, so the full stream would
    // be roughly the whole userbase — and the loudest issues (500s from the
    // interceptor, the WebKit ERR_NETWORK cluster) are the ones that scale
    // with it. Rates are visible in the dashboard: sendClientReports below
    // makes the SDK report what it discarded, with reason "sample_rate".
    // Raise this when hunting something rare; a 1-in-5 chance per event means
    // a low-frequency error can vanish entirely.
    sampleRate: 0.2,
    // No IP address, cookies or request headers attached to events. Every
    // visitor reports now, including logged-out EU traffic that never consented
    // to anything, and the privacy policy only declares Sentry for the mobile
    // app. Turning this back on means updating that policy first.
    sendDefaultPii: false,
    // Replay is off: with neither replaysSessionSampleRate nor
    // replaysOnErrorSampleRate set, the SDK disables it and logs as much. The
    // integration is left out rather than registered dead, because registering
    // it pulls @sentry-internal/replay into the bundle for every visitor.
    // Turning replay back on means adding a sample rate AND the integration.
    allowUrls: [/https?:\/\/([a-z0-9-]+\.)?easysbc\.io/],
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [import.meta.env.VITE_APP_SENTRY_FILTERING_KEY],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
        // behaviour: "drop-error-if-contains-third-party-frames",
        // The SDK's timer/listener wrapper lives in our bundle; without this
        // every ad error thrown in a callback counts as first-party.
        ignoreSentryInternalFrames: true,
      }),
    ],
    sendClientReports: true,
    beforeSend(event, hint) {
      if (!filterSentryNoise(event, hint)) return null;
      try {
        event.tags = { ...event.tags, route: location.pathname };
      } catch {}
      return event;
    },
  });
}

// English resources are inline so this resolves synchronously; a translated
// locale fetches its chunk first, so the first paint is already in that language.
if (!languageRedirect) {
  initI18n(uiLocale).then(() => {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <Provider store={store}>
        <HelmetProvider>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </HelmetProvider>
      </Provider>,
    );
  });
}

preloadMixpanel();
